import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { FaBell, FaChevronDown, FaChevronUp, FaStar } from 'react-icons/fa';
import { buildCatalogNodeListingPath } from '../../utils/partDetailRoutes';

function DeliveryLabel({ fee }) {
  if (fee === 0 || fee == null) return <span className="text-emerald-600 font-semibold">Free</span>;
  return <span>₹{Number(fee).toLocaleString('en-IN')}</span>;
}

function RatingLabel({ listing }) {
  if (listing.sellerRatingLabel === 'New seller' || listing.sellerRating == null) {
    return <span className="text-slate-500">New seller</span>;
  }
  return (
    <span className="inline-flex items-center gap-1">
      <FaStar className="text-amber-400 text-xs" />
      {Number(listing.sellerRating).toFixed(1)}
    </span>
  );
}

export default function SellerRowCard({
  listing,
  catalogNodeId,
  vehicleId,
  activeTab,
  onNotify,
  notifyLoading,
}) {
  const [expanded, setExpanded] = useState(false);
  const unavailable = listing.available === false;
  const detailPath = buildCatalogNodeListingPath(listing, {
    vehicleId,
    catalogNodeId,
    condition: listing.condition || activeTab,
  });

  const priceIndicator = listing.priceIndicator;
  const isCheapest = priceIndicator?.type === 'cheapest';

  const cardBody = (
    <>
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="min-w-0 flex-1">
          <div className="flex flex-wrap items-center gap-2 mb-1.5">
            <span
              className={`text-[10px] font-bold uppercase px-2 py-0.5 rounded ${
                unavailable ? 'bg-slate-100 text-slate-400' : 'bg-slate-100 text-slate-700'
              }`}
            >
              {listing.conditionLabel || listing.condition}
            </span>
            {listing.badges?.map((badge) => (
              <span
                key={badge}
                className={`text-[10px] font-semibold px-2 py-0.5 rounded-full ${
                  unavailable
                    ? 'bg-slate-50 text-slate-400'
                    : 'bg-indigo-50 text-indigo-700'
                }`}
              >
                {badge}
              </span>
            ))}
          </div>

          <p
            className={`text-xl font-bold ${
              unavailable ? 'text-slate-400 line-through' : 'text-orange-600'
            }`}
          >
            ₹{Number(listing.price).toLocaleString('en-IN')}
            {!unavailable && listing.mrpDisplay?.strikethrough && (
              <span className="ml-2 text-sm font-normal text-slate-400 line-through">
                ₹{Number(listing.mrpDisplay.mrp).toLocaleString('en-IN')}
              </span>
            )}
            {!unavailable && !listing.mrpDisplay && listing.mrp && listing.price < listing.mrp && (
              <span className="ml-2 text-sm font-normal text-slate-400 line-through">
                ₹{Number(listing.mrp).toLocaleString('en-IN')}
              </span>
            )}
          </p>

          {!unavailable && priceIndicator && (
            <p
              className={`text-xs font-semibold mt-1 ${
                isCheapest
                  ? 'inline-block rounded-md bg-emerald-50 text-emerald-700 px-2 py-0.5'
                  : 'text-slate-500'
              }`}
            >
              {priceIndicator.label}
            </p>
          )}

          {unavailable && (
            <p className="text-xs font-medium text-slate-400 mt-1">
              Unavailable — {listing.unavailableReason || 'Out of stock'}
            </p>
          )}
        </div>

        <div
          className={`text-right text-sm space-y-1 shrink-0 ${
            unavailable ? 'text-slate-400' : 'text-slate-600'
          }`}
        >
          <p className="flex items-center justify-end gap-1">
            <RatingLabel listing={listing} />
          </p>
          <p>{listing.city}</p>
          {listing.distanceKm != null && (
            <p className="text-xs text-slate-400">{listing.distanceKm} km away</p>
          )}
          <p className="text-xs">{listing.dispatchWindow}</p>
          <p className="text-xs">
            Delivery <DeliveryLabel fee={listing.deliveryFee} />
          </p>
        </div>
      </div>

      {expanded && listing.detail && (
        <div className="mt-4 pt-4 border-t border-slate-100 space-y-4">
          {listing.detail.photos?.length > 0 && (
            <div className="flex gap-2 overflow-x-auto pb-1">
              {listing.detail.photos.map((url) => (
                <img
                  key={url}
                  src={url}
                  alt=""
                  className="h-16 w-16 rounded-lg object-cover border border-slate-200 shrink-0"
                />
              ))}
            </div>
          )}

          {listing.detail.description && (
            <p className="text-sm text-slate-600">{listing.detail.description}</p>
          )}

          {listing.alsoFitsLabel && (
            <p className="text-xs font-medium text-indigo-700 bg-indigo-50 rounded-lg px-3 py-2">
              {listing.alsoFitsLabel}
            </p>
          )}

          <div>
            <p className="text-xs font-bold uppercase text-slate-400 mb-2">Dispatch timeline</p>
            <ol className="space-y-2">
              {listing.detail.dispatchTimeline?.map((step) => (
                <li key={step.step} className="flex items-start justify-between gap-2 text-sm">
                  <span className="text-slate-700">{step.step}</span>
                  <span className="text-slate-400 text-xs shrink-0">{step.eta}</span>
                </li>
              ))}
            </ol>
          </div>

          {!unavailable && (
            <Link
              to={detailPath}
              className="inline-flex items-center justify-center rounded-lg bg-indigo-600 text-white px-4 py-2 text-sm font-semibold hover:bg-indigo-700"
            >
              View full listing &amp; buy
            </Link>
          )}
        </div>
      )}

      <div className="mt-3 flex flex-wrap items-center justify-between gap-2">
        {unavailable ? (
          <button
            type="button"
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              onNotify?.(listing);
            }}
            disabled={notifyLoading}
            className="inline-flex items-center gap-2 rounded-full border border-slate-300 bg-white px-4 py-1.5 text-xs font-semibold text-slate-700 hover:border-indigo-400 disabled:opacity-60"
          >
            <FaBell className="text-indigo-500" />
            {notifyLoading ? 'Saving…' : 'Notify me'}
          </button>
        ) : (
          <button
            type="button"
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              setExpanded((v) => !v);
            }}
            className="inline-flex items-center gap-1 text-xs font-semibold text-indigo-600 hover:text-indigo-800"
          >
            {expanded ? (
              <>
                <FaChevronUp /> Hide details
              </>
            ) : (
              <>
                <FaChevronDown /> Show listing details
              </>
            )}
          </button>
        )}
      </div>
    </>
  );

  if (unavailable) {
    return (
      <article
        className="rounded-xl border border-slate-200 bg-slate-50 p-4 opacity-75"
        aria-label="Unavailable seller listing"
      >
        {cardBody}
      </article>
    );
  }

  return (
    <article className="rounded-xl border border-slate-200 bg-white p-4 hover:border-indigo-300 hover:shadow-sm transition">
      {cardBody}
    </article>
  );
}
